/**
 * ログ画面の選択肢とラベル(`server/app/idp/constants.py` と同じ値)。
 *
 * **真実はサーバー側**。行のラベル(`eventTypeLabel` など)は API が返すので、
 * ここに持つのは絞り込みの選択肢と、期間のプリセットだけ。
 */
import dayjs, { type Dayjs } from "dayjs";

export const LOG_TABS = [
  { key: "access", label: "アクセスログ" },
  { key: "user-ops", label: "ユーザー操作ログ" },
  { key: "admin-ops", label: "管理者操作ログ" },
  { key: "sync", label: "同期ログ" },
  { key: "bulk", label: "一括登録ログ" },
  // R7d。拒否と追加認証だけが残る(許可は記録しない)
  { key: "policy", label: "ポリシー判定" },
] as const;

export type LogTabKey = (typeof LOG_TABS)[number]["key"];

export const LOG_SUCCESS = "success";
export const LOG_FAILURE = "failure";

export const RESULT_OPTIONS = [
  { value: LOG_SUCCESS, label: "成功" },
  { value: LOG_FAILURE, label: "失敗" },
];

/** 認証ログの種別(よく使うものだけ。空欄なら全部)。 */
export const ACCESS_TYPE_OPTIONS = [
  { value: "LOGIN", label: "ログイン" },
  { value: "LOGIN_ERROR", label: "ログイン失敗" },
  { value: "LOGOUT", label: "ログアウト" },
  { value: "CODE_TO_TOKEN", label: "トークン発行(SSO)" },
  { value: "REFRESH_TOKEN", label: "トークン更新" },
  { value: "CLIENT_LOGIN", label: "API クライアント認証" },
  { value: "IDENTITY_PROVIDER_LOGIN", label: "外部 IdP でログイン" },
  { value: "IMPERSONATE", label: "代理ログイン" },
];

/** ユーザー操作ログの種別。 */
export const USER_OPS_TYPE_OPTIONS = [
  { value: "UPDATE_PASSWORD", label: "パスワード変更" },
  { value: "UPDATE_PROFILE", label: "プロフィール変更" },
  { value: "UPDATE_EMAIL", label: "メールアドレス変更" },
  { value: "UPDATE_TOTP", label: "ワンタイムパスワードの登録" },
  { value: "REMOVE_TOTP", label: "ワンタイムパスワードの解除" },
  { value: "SEND_RESET_PASSWORD", label: "パスワード再設定メールの送信" },
  { value: "RESET_PASSWORD", label: "パスワード再設定" },
  { value: "VERIFY_EMAIL", label: "メールアドレスの確認" },
  { value: "EXECUTE_ACTIONS", label: "必須アクションの実行" },
];

/** 管理者操作ログの出所。 */
export const SOURCE_OPTIONS = [
  { value: "suite", label: "スイート" },
  { value: "keycloak", label: "Keycloak" },
];

/** 同期ログのタスク(`logs_service.SYNC_TASKS` と同じ値)。 */
export const SYNC_TASK_OPTIONS = [
  { value: "reconcile", label: "アカウント突合" },
  { value: "task", label: "自動タスク(発行・停止・匿名化)" },
];

/** 一括登録ログの状態(`C.BULK_STATUS_LABEL` と同じ値)。 */
export const BULK_STATUS_OPTIONS = [
  { value: "running", label: "実行中" },
  { value: "done", label: "完了" },
  { value: "failed", label: "失敗" },
  { value: "aborted", label: "中断" },
];

/** 期間のプリセット(日数)。既定は 7 日で、サーバー側の既定と同じ。 */
export const RANGE_PRESETS = [
  { days: 1, label: "今日" },
  { days: 7, label: "7 日" },
  { days: 30, label: "30 日" },
];

export const DEFAULT_RANGE_DAYS = 7;

export type Range = [Dayjs, Dayjs];

/** 直近 n 日。`1`(今日)はその日の 0 時から。 */
export function lastDays(days: number): Range {
  const end = dayjs();
  return [days <= 1 ? end.startOf("day") : end.subtract(days, "day"), end];
}

/** API に渡す `from` / `to`。ISO 8601(オフセット付き)で送る。 */
export function rangeParams(range: Range): { from: string; to: string } {
  return { from: range[0].toISOString(), to: range[1].toISOString() };
}
